/*
  Toast system inspired by Sonner API. Exposes an imperative `toast` API and a `Toaster` host.
  - toast(message, options?)
  - toast.success/error/warning/info
  - toast.custom(render, options?)
  - toast.dismiss(id?)
  - toast.promise(promise, { loading, success, error }, options?)

  Usage:
    <Toaster position="top-right" />
    toast('Saved');
    toast.success('Success', { description: 'All good' });
*/

'use client';

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, animate } from 'motion/react';
import type { Transition } from 'motion/react';
import { Icon } from '../../icons';
import './Toast.css';

type ToastType = 'default' | 'success' | 'error' | 'warning' | 'info' | 'loading' | 'custom';

export type ToastId = string;

export interface ToastAction {
  label: string;
  onClick?: (id: ToastId) => void;
  ariaLabel?: string;
}

export interface ToastOptions {
  id?: ToastId;
  description?: React.ReactNode;
  duration?: number; // ms
  dismissible?: boolean;
  action?: ToastAction | null;
  icon?: React.ReactNode;
  onDismiss?: (id: ToastId) => void;
  // Visual
  className?: string;
  showProgress?: boolean;
}

export interface ShowToastOptions extends ToastOptions {
  type?: ToastType;
}

export interface ToastRecord extends Required<Pick<ShowToastOptions, 'type'>> {
  id: ToastId;
  title?: React.ReactNode;
  description?: React.ReactNode;
  duration: number; // ms
  createdAt: number;
  dismissible: boolean;
  icon?: React.ReactNode;
  action?: ToastAction | null;
  className?: string;
  onDismiss?: (id: ToastId) => void;
  // internal
  _pauseAt?: number | null;
  render?: (id: ToastId) => React.ReactNode;
  showProgress: boolean;
}

type Listener = (records: ToastRecord[]) => void;

// Simple global store
const store = (() => {
  let records: ToastRecord[] = [];
  const listeners = new Set<Listener>();
  const notify = () => listeners.forEach((l) => l(records));

  function upsert(record: ToastRecord) {
    const existingIdx = records.findIndex((r) => r.id === record.id);
    if (existingIdx >= 0) {
      records = [...records.slice(0, existingIdx), record, ...records.slice(existingIdx + 1)];
    } else {
      records = [...records, record];
    }
    notify();
  }

  function remove(id: ToastId) {
    const toRemove = records.find((r) => r.id === id);
    records = records.filter((r) => r.id !== id);
    notify();
    toRemove?.onDismiss?.(id);
  }

  function subscribe(listener: Listener) {
    listeners.add(listener);
    // Push a new reference to ensure initial render in React effect timing
    listener([...records]);
    return () => {
      listeners.delete(listener);
    };
  }

  return { upsert, remove, subscribe, get: () => records };
})();

function generateId(): ToastId {
  return Math.random().toString(36).slice(2, 9);
}

const DEFAULT_DURATION = 3500;

export type ToastAPI = {
  (title: React.ReactNode, options?: ToastOptions): ToastId;
  success: (title: React.ReactNode, options?: ToastOptions) => ToastId;
  error: (title: React.ReactNode, options?: ToastOptions) => ToastId;
  warning: (title: React.ReactNode, options?: ToastOptions) => ToastId;
  info: (title: React.ReactNode, options?: ToastOptions) => ToastId;
  loading: (title: React.ReactNode, options?: ToastOptions) => ToastId;
  custom: (
    render: (id: ToastId) => React.ReactNode,
    options?: Omit<ToastOptions, 'icon' | 'action' | 'dismissible'> & { dismissible?: boolean },
  ) => ToastId;
  dismiss: (id?: ToastId) => void;
  promise: <T>(
    promise: Promise<T>,
    states: {
      loading: React.ReactNode;
      success: React.ReactNode | ((value: T) => React.ReactNode);
      error: React.ReactNode | ((err: unknown) => React.ReactNode);
    },
    options?: ToastOptions,
  ) => Promise<T>;
};

export const toast: ToastAPI = Object.assign(
  (title: React.ReactNode, options: ToastOptions = {}) => createToast('default', title, options),
  {
    success: (title: React.ReactNode, options?: ToastOptions) =>
      createToast('success', title, options),
    error: (title: React.ReactNode, options?: ToastOptions) => createToast('error', title, options),
    warning: (title: React.ReactNode, options?: ToastOptions) =>
      createToast('warning', title, options),
    info: (title: React.ReactNode, options?: ToastOptions) => createToast('info', title, options),
    loading: (title: React.ReactNode, options?: ToastOptions) =>
      createToast('loading', title, { duration: Infinity, ...options }),
    custom: (
      render: (id: ToastId) => React.ReactNode,
      options?: Omit<ToastOptions, 'icon' | 'action' | 'dismissible'> & { dismissible?: boolean },
    ) => createToast('custom', render, options ?? {}),
    dismiss: (id?: ToastId) => {
      if (id) store.remove(id);
      else store.get().forEach((r) => store.remove(r.id));
    },
    promise: async <T,>(
      promise: Promise<T>,
      states: {
        loading: React.ReactNode;
        success: React.ReactNode | ((value: T) => React.ReactNode);
        error: React.ReactNode | ((err: unknown) => React.ReactNode);
      },
      options?: ToastOptions,
    ) => {
      const id = toast.loading(states.loading, options);
      try {
        const value = await promise;
        const successContent =
          typeof states.success === 'function'
            ? (states.success as (v: T) => React.ReactNode)(value)
            : states.success;
        // Drop transient loading description like "Please wait" by default when moving to success
        const { description: _drop, ...rest } = options ?? {};
        void _drop;
        createToast('success', successContent, { ...rest, id });
        return value;
      } catch (err) {
        const errorContent =
          typeof states.error === 'function'
            ? (states.error as (e: unknown) => React.ReactNode)(err)
            : states.error;
        const { description: _drop, ...rest } = options ?? {};
        void _drop;
        createToast('error', errorContent, { ...rest, id });
        throw err;
      }
    },
  },
) as ToastAPI;

// Overloads to support a function renderer for custom toasts
function createToast(
  type: 'custom',
  content: (id: ToastId) => React.ReactNode,
  options?: ToastOptions,
): ToastId;
function createToast(
  type: Exclude<ToastType, 'custom'>,
  content: React.ReactNode,
  options?: ToastOptions,
): ToastId;
function createToast(
  type: ToastType,
  content: React.ReactNode | ((id: ToastId) => React.ReactNode),
  options: ToastOptions = {},
): ToastId {
  const id = options.id ?? generateId();
  const isCustom = type === 'custom' && typeof content === 'function';
  const record: ToastRecord = {
    id,
    type,
    title: isCustom ? undefined : (content as React.ReactNode),
    description: options.description,
    duration: Number.isFinite(options.duration ?? DEFAULT_DURATION)
      ? (options.duration as number)
      : DEFAULT_DURATION,
    createdAt: Date.now(),
    dismissible: options.dismissible ?? true,
    icon: options.icon,
    action: options.action ?? null,
    className: options.className,
    onDismiss: options.onDismiss,
    showProgress: options.showProgress ?? true,
  };

  store.upsert(record);

  // For custom, we store a placeholder and render via registry below
  if (isCustom) {
    // attach renderer and upsert again
    (record as ToastRecord).render = content as (id: ToastId) => React.ReactNode;
    store.upsert(record);
  }

  return id;
}

export type ToasterPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface ToasterProps {
  position?: ToasterPosition;
  visibleToasts?: number;
  gap?: number; // px gap between items
  closeButton?: boolean;
  expand?: boolean; // full width on mobile
}

export function Toaster({
  position = 'top-right',
  visibleToasts = 8,
  gap = 10,
  closeButton = true,
  expand = true,
}: ToasterProps) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  useEffect(() => store.subscribe(setToasts), []);

  const containerStyle: React.CSSProperties = useMemo(() => {
    const [vertical, horizontal] = position.split('-') as [
      'top' | 'bottom',
      'left' | 'center' | 'right',
    ];
    const style: React.CSSProperties = {
      position: 'fixed',
      insetInlineStart: horizontal === 'left' ? 12 : undefined,
      insetInlineEnd: horizontal === 'right' ? 12 : undefined,
      left: horizontal === 'center' ? '50%' : undefined,
      transform: horizontal === 'center' ? 'translateX(-50%)' : undefined,
      top: vertical === 'top' ? 12 : undefined,
      bottom: vertical === 'bottom' ? 12 : undefined,
      display: 'flex',
      flexDirection: 'column',
      gap,
      zIndex: 9999,
      pointerEvents: 'none',
      width: expand ? 'min(420px, 92vw)' : 'auto',
    };
    return style;
  }, [position, gap, expand]);

  const limited = useMemo(() => toasts.slice(-visibleToasts), [toasts, visibleToasts]);

  return (
    <div className="toaster" style={containerStyle} aria-live="polite" aria-atomic>
      <AnimatePresence initial={false}>
        {limited.map((t) => (
          <ToastItem key={t.id} record={t} closeButton={closeButton} position={position} />
        ))}
      </AnimatePresence>
    </div>
  );
}

interface ToastItemProps {
  record: ToastRecord;
  closeButton: boolean;
  position: ToasterPosition;
}

function useCountdown(record: ToastRecord) {
  const [remaining, setRemaining] = useState(record.duration);
  const pauseRef = useRef<number | null>(null);
  const startRef = useRef<number>(Date.now());
  const rafRef = useRef<number | null>(null);
  const isInfinite = !Number.isFinite(record.duration) || record.duration === Infinity;

  useEffect(() => {
    if (isInfinite) return;
    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      setRemaining(Math.max(0, record.duration - elapsed));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [record.duration, isInfinite]);

  const pause = () => {
    if (isInfinite || pauseRef.current != null) return;
    pauseRef.current = Date.now();
  };
  const resume = () => {
    if (isInfinite || pauseRef.current == null) return;
    const pausedFor = Date.now() - pauseRef.current;
    startRef.current += pausedFor;
    pauseRef.current = null;
  };

  return { remaining, pause, resume } as const;
}

function ToastItem({ record, closeButton, position }: ToastItemProps) {
  const { remaining, pause, resume } = useCountdown(record);
  const willDismiss =
    remaining === 0 && Number.isFinite(record.duration) && record.duration !== Infinity;
  const [hovered, setHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const measuredRef = useRef<HTMLDivElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number>(0);
  const [animateHeight, setAnimateHeight] = useState<boolean>(false);
  const prevTypeRef = useRef<ToastType>(record.type);
  const prevDescRef = useRef<boolean>(!!record.description);

  useEffect(() => {
    if (willDismiss) store.remove(record.id);
  }, [willDismiss, record.id]);

  const vertical = position.startsWith('top') ? 'top' : 'bottom';
  const horizontal: 'left' | 'center' | 'right' = position.endsWith('left')
    ? 'left'
    : position.endsWith('right')
      ? 'right'
      : 'center';
  const axis: 'x' | 'y' = horizontal === 'center' ? 'y' : 'x';

  const spring: Transition = { type: 'spring', stiffness: 500, damping: 38 };

  const iconNode = useMemo(() => {
    if (record.icon) return record.icon;
    const map: Record<ToastType, React.ReactNode> = {
      default: <Icon name="Info" color="neutral-dark" aria-hidden />,
      info: <Icon name="Info" color="info" aria-hidden />,
      success: <Icon name="CheckCircle" color="success" aria-hidden />,
      error: <Icon name="XCircle" color="error" aria-hidden />,
      warning: <Icon name="AlertTriangle" color="warning" aria-hidden />,
      loading: <div className="toast__spinner" aria-hidden />,
      custom: null,
    };
    return map[record.type];
  }, [record.icon, record.type]);

  const progress = useMemo(() => {
    if (!Number.isFinite(record.duration) || record.duration === Infinity) return 0;
    return Math.max(0, Math.min(1, remaining / record.duration));
  }, [remaining, record.duration]);

  const ariaRole = record.type === 'error' ? 'alert' : 'status';

  // Smooth height transition by measuring dynamic content
  useLayoutEffect(() => {
    const node = measuredRef.current;
    if (!node) return;
    const update = () => {
      const rect = node.getBoundingClientRect();
      setMeasuredHeight(rect.height);
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(node);
    return () => ro.disconnect();
  }, [record.type, record.title, record.description]);

  // Only animate height on meaningful content transition (e.g., loading -> success/error or description removed)
  useEffect(() => {
    const prevType = prevTypeRef.current;
    const prevDesc = prevDescRef.current;
    const currType = record.type;
    const currDesc = !!record.description;

    const transitionedFromLoading =
      prevType === 'loading' && (currType === 'success' || currType === 'error');
    const descriptionCollapsed = prevDesc && !currDesc;

    // Avoid animating on initial mount
    const isInitial = prevType === currType && prevDesc === currDesc;
    setAnimateHeight(!isInitial && (transitionedFromLoading || descriptionCollapsed));

    prevTypeRef.current = currType;
    prevDescRef.current = currDesc;
  }, [record.type, record.description]);

  // Manual swipe handling for robust cross-lib support
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    let startX = 0;
    let startY = 0;
    let dragging = false;
    let pointerId: number | null = null;
    let startTime = 0;

    const onPointerDown = (e: PointerEvent) => {
      // Only left button / primary touch
      if (pointerId !== null) return;
      const target = e.target as Element | null;
      if (
        target &&
        target.closest(
          '.toast__close, .toast__action, button, a, [role="button"], input, textarea, select, label',
        )
      ) {
        return; // allow native click on interactive elements
      }
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      startTime = Date.now();
      dragging = true;
      node.setPointerCapture?.(e.pointerId);
      pause();
      setHovered(true);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!dragging || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const value = axis === 'x' ? dx : dy;
      const transform = axis === 'x' ? `translateX(${value}px)` : `translateY(${value}px)`;
      (node as HTMLElement).style.transform = transform;
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!dragging || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const dt = Math.max(1, Date.now() - startTime);
      const vx = (dx / dt) * 1000; // px/s approx
      const vy = (dy / dt) * 1000;

      const distance = axis === 'x' ? dx : dy;
      const velocity = axis === 'x' ? vx : vy;

      const distanceThreshold = 80;
      const velocityThreshold = 500;

      const shouldDismiss = (() => {
        if (axis === 'x') {
          if (horizontal === 'left')
            return distance < -distanceThreshold || velocity < -velocityThreshold;
          if (horizontal === 'right')
            return distance > distanceThreshold || velocity > velocityThreshold;
          return Math.abs(distance) > distanceThreshold || Math.abs(velocity) > velocityThreshold;
        } else {
          if (vertical === 'top')
            return distance < -distanceThreshold || velocity < -velocityThreshold;
          return distance > distanceThreshold || velocity > velocityThreshold; // bottom
        }
      })();

      const animateOut = async () => {
        const off =
          axis === 'x'
            ? horizontal === 'left'
              ? -500
              : horizontal === 'right'
                ? 500
                : Math.sign(distance) * 500
            : vertical === 'top'
              ? -300
              : 300;
        await animate(node, axis === 'x' ? { x: off, opacity: 0 } : { y: off, opacity: 0 }, {
          duration: 0.2,
          ease: 'easeIn',
        }).finished;
      };

      if (shouldDismiss) {
        animateOut().finally(() => store.remove(record.id));
      } else {
        animate(node, { x: 0, y: 0, opacity: 1 }, { duration: 0.2, ease: 'easeOut' });
        resume();
        setHovered(false);
      }

      dragging = false;
      pointerId = null;
      node.releasePointerCapture?.(e.pointerId);
    };

    node.addEventListener('pointerdown', onPointerDown);
    node.addEventListener('pointermove', onPointerMove);
    node.addEventListener('pointerup', onPointerUp);
    node.addEventListener('pointercancel', onPointerUp);
    return () => {
      node.removeEventListener('pointerdown', onPointerDown);
      node.removeEventListener('pointermove', onPointerMove);
      node.removeEventListener('pointerup', onPointerUp);
      node.removeEventListener('pointercancel', onPointerUp);
    };
  }, [axis, horizontal, pause, resume, vertical, record.id]);

  return (
    <motion.div
      ref={containerRef}
      layout
      initial={{ opacity: 0, y: vertical === 'top' ? -20 : 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: vertical === 'top' ? -8 : 8, scale: 0.98, filter: 'blur(4px)' }}
      transition={spring}
      className={`toast toast--${record.type}${record.className ? ` ${record.className}` : ''}`}
      onMouseEnter={() => {
        setHovered(true);
        pause();
      }}
      onMouseLeave={() => {
        setHovered(false);
        resume();
      }}
      role={ariaRole}
      aria-live={record.type === 'error' ? 'assertive' : 'polite'}
      style={{ pointerEvents: 'auto', touchAction: axis === 'x' ? 'pan-y' : 'pan-x' }}
    >
      <div className="toast__body">
        {iconNode && <div className="toast__icon">{iconNode}</div>}
        <motion.div className="toast__content" layout>
          <motion.div
            className="toast__content-outer"
            style={{ overflow: 'hidden', willChange: 'height' }}
            initial={false}
            animate={{ height: measuredHeight }}
            transition={
              animateHeight ? { duration: 0.3, ease: [0.16, 1, 0.3, 1] } : { duration: 0 }
            }
          >
            <div ref={measuredRef}>
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={
                    record.type === 'custom' ? `custom-${record.id}` : `${record.type}-${record.id}`
                  }
                  initial={{
                    y: -8,
                    opacity: 0,
                    filter: 'blur(6px)',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                  }}
                  animate={{
                    y: 0,
                    opacity: 1,
                    filter: 'blur(0px)',
                    position: 'relative',
                    scale: 1,
                  }}
                  exit={{
                    y: 4,
                    opacity: 0,
                    filter: 'blur(4px)',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                  }}
                  transition={{ duration: animateHeight ? 0.2 : 0.14, ease: [0.16, 1, 0.3, 1] }}
                  style={{ willChange: 'transform, filter, opacity', transformOrigin: '50% 50%' }}
                >
                  {record.type === 'custom' && record.render ? (
                    record.render(record.id)
                  ) : (
                    <>
                      {record.title}
                      <AnimatePresence initial={false} mode="wait">
                        {record.description ? (
                          <motion.div
                            key="desc"
                            className="toast__description"
                            initial={{ scaleY: 0.6, opacity: 0, filter: 'blur(6px)' }}
                            animate={{ scaleY: 1, opacity: 1, filter: 'blur(0px)' }}
                            exit={{ scaleY: 0.8, opacity: 0, filter: 'blur(4px)' }}
                            transition={{ duration: 0.12, ease: [0.4, 0, 1, 1] }}
                            style={{
                              transformOrigin: '50% 50%',
                              willChange: 'transform, filter, opacity',
                            }}
                          >
                            {record.description}
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
        {record.action && (
          <button
            className="toast__action"
            onClick={() => record.action?.onClick?.(record.id)}
            aria-label={
              record.action.ariaLabel ??
              (typeof record.action.label === 'string' ? record.action.label : 'Action')
            }
          >
            {record.action.label}
          </button>
        )}
        {closeButton && record.dismissible && (
          <button
            className="toast__close"
            aria-label="Dismiss"
            onClick={() => store.remove(record.id)}
          >
            <Icon name="X" color="neutral-dark" aria-hidden />
          </button>
        )}
      </div>

      {record.showProgress && Number.isFinite(record.duration) && record.duration !== Infinity && (
        <div
          className={`toast__progress${hovered ? ' is-paused' : ''}`}
          style={{
            transform: `scaleX(${progress})`,
          }}
        />
      )}
    </motion.div>
  );
}

export default Toaster;
